import { Divider, Switch, Flex, Checkbox } from "antd";
import { AuditOutlined, BellFilled, DeleteOutlined, FileSearchOutlined, FireFilled, SettingFilled } from "@ant-design/icons";
import { CustomDivider, Description, GeneralSettingsContainer, MainSettingTitle, SettingWrapper, Title } from "./styles"
import GeneralSetting from "./GeneralSetting";
import PricingConfiguration from "./PricingConfiguration";
import PaymentConfiguration from "./PaymentConfiguration";
import SchedulerSettings from "./SchedulerSettings";
import UnpaidCleanupSettings from "./UnpaidCleanupSettings";
import LanguageConfiguration from "./LanguageConfiguration";
// LandingPageConfiguration moved to per-establishment-location editor (EstablishmentLocationModal,
// "Airport Content" tab, under "Search Details Grid"). The file is kept on disk so the existing
// global landingHeroContent / landingTimeNotContent DB columns can still be re-exposed later if
// product changes its mind, but the section no longer renders in /keys-and-settings.
// import LandingPageConfiguration from "./LandingPageConfiguration";
import { useAppDispatch, useAppSelector } from "../../utils/hooks/redux";
import { updateEstablishment } from "../../redux/features/user/userAsyncThunk";
import { LostStatuses } from "../../types/LostStatuses";
import { getStatusTheme } from "../Reports/Board/statusThemes";

const BoardSettings: React.FC = () => {
    const dispatch = useAppDispatch();
    const { establishment, loading } = useAppSelector((state) => state.users);

    const handleToggle = async (field: string, checked: boolean) => {
        await dispatch(updateEstablishment({ formData: { [field]: checked } as any })).unwrap();
    };

    // Each entry mirrors a kanban column. Icon + theme are pulled from the same source the
    // actual board uses (statusThemes + columnConfig), so the picker visually matches what
    // the admin will see. Render order = visual kanban order, left to right.
    const columnToggles = [
        { field: 'showOpenOnBoard',       status: LostStatuses.Open,       Icon: BellFilled,        defaultOn: true },
        { field: 'showReviewOnBoard',     status: LostStatuses.Review,     Icon: AuditOutlined,     defaultOn: true },
        { field: 'showInProcessOnBoard',  status: LostStatuses.InProcess,  Icon: SettingFilled,     defaultOn: true },
        { field: 'showLFNotifiedOnBoard', status: LostStatuses.LFNotified, Icon: FileSearchOutlined, defaultOn: true },
        { field: 'showUrgentOnBoard',     status: LostStatuses.Urgent,     Icon: FireFilled,        defaultOn: true },
        { field: 'showDeletedOnBoard',    status: LostStatuses.Deleted,    Icon: DeleteOutlined,    defaultOn: false },
    ];

    return (
        <GeneralSettingsContainer>
            <Title>Board Settings</Title>
            <Description>Configure which columns are visible on the reports board.</Description>
            <div
                style={{
                    marginTop: 24,
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                    gap: 12,
                }}
            >
                {columnToggles.map(({ field, status, Icon, defaultOn }) => {
                    const theme = getStatusTheme(status);
                    const isChecked = (establishment as any)?.[field] ?? defaultOn;
                    return (
                        <label
                            key={field}
                            htmlFor={`board-toggle-${field}`}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                padding: '10px 12px',
                                border: `1px solid ${isChecked ? theme.border : '#E5E7EB'}`,
                                background: isChecked ? theme.background : '#FAFAFA',
                                borderRadius: 8,
                                cursor: loading ? 'not-allowed' : 'pointer',
                                opacity: loading ? 0.6 : 1,
                                transition: 'background 120ms, border-color 120ms, opacity 120ms',
                                userSelect: 'none',
                            }}
                        >
                            <Checkbox
                                id={`board-toggle-${field}`}
                                checked={isChecked}
                                disabled={loading}
                                onChange={(e) => handleToggle(field, e.target.checked)}
                            />
                            <span
                                aria-hidden
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 24,
                                    height: 24,
                                    borderRadius: 6,
                                    background: theme.pillBackground,
                                    color: theme.pillColor,
                                    flexShrink: 0,
                                }}
                            >
                                <Icon style={{ fontSize: 14 }} />
                            </span>
                            <span style={{ fontWeight: 500, color: '#1F2937' }}>{theme.label}</span>
                        </label>
                    );
                })}
            </div>
        </GeneralSettingsContainer>
    );
};

const Setting: React.FC = () => {
    return (
        <>
            <SettingWrapper>
                <MainSettingTitle>
                    Settings
                </MainSettingTitle>
            </SettingWrapper>
            <GeneralSetting />
            <CustomDivider />
            <BoardSettings />
            <CustomDivider />
            {/*<PricingConfiguration />*/}
            {/*<CustomDivider />*/}
            {/*<PaymentConfiguration />*/}
            {/*<CustomDivider />*/}
            <SchedulerSettings />
            <CustomDivider />
            <UnpaidCleanupSettings />
            <CustomDivider />
            <LanguageConfiguration />
        </>
    )
}

export default Setting
